import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  ChevronDown,
  CircleX,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

const multiSelectVariants = cva(
  "rounded-full border text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 px-4 py-2 h-10 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cyan-500/20",
  {
    variants: {
      variant: {
        default: "border-gray-200 text-gray-800",
        active: "border-cyan-400 bg-cyan-50/40 text-cyan-900 shadow-xs",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface Option {
  label: string;
  value: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof multiSelectVariants> {
  options: Option[];
  onValueChange: (value: string[]) => void;
  defaultValue?: string[];
  placeholder?: string;
  popoverTitle?: string;
  className?: string;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      defaultValue = [],
      placeholder = "Office Type",
      popoverTitle = "Office Type",
      className,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue);
    const [isOpen, setIsOpen] = React.useState(false);

    const toggleOption = (value: string) => {
      const updatedValues = selectedValues.includes(value)
        ? selectedValues.filter((item) => item !== value)
        : [...selectedValues, value];

      setSelectedValues(updatedValues);
      onValueChange(updatedValues);
    };

    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    const isSelectedActive = selectedValues.length > 0 || isOpen;

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            className={cn(
              "rounded-full border text-sm font-medium transition-colors duration-200",
              "flex items-center gap-2 px-3 py-1.5 h-10",
              "focus:outline-none focus:ring-0 focus-visible:ring-0",
              selectedValues.length > 0
                ? "bg-oh-black text-white hover:bg-oh-black hover:text-white"
                : "bg-white text-black hover:bg-white hover:text-black",
              className
            )}
            {...props}
          >
            {/* Label */}
            <span className="truncate">
              {selectedValues.length === 1
                ? options.find(
                  (option) => option.value === selectedValues[0]
                )?.label
                : placeholder}
            </span>

            {/* Multiple selected - white count circle */}
            {selectedValues.length > 1 && (
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-[#333333]">
                {selectedValues.length}
              </span>
            )}

            {/* Single selected - white outlined cross circle */}
            {selectedValues.length === 1 && (
              <button
                type="button"
                aria-label="Clear selection"
                className="flex shrink-0 items-center rounded-full justify-center text-white transition-opacity hover:bg-white/20 focus:outline-none"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setSelectedValues([]);
                  onValueChange([]);
                  setIsOpen(false);
                }}
              >
                <CircleX className="h-6 w-6" />
              </button>
            )}

            {/* No selection - static chevron */}
            {selectedValues.length === 0 && (
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 transition-transform duration-200 text-oh-black",
                  isOpen && "rotate-180"
                )}
              />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-full min-w-[340px] sm:min-w-[540px] rounded-2xl p-6 shadow-xl border-gray-100 bg-white"
        >
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-lg font-bold text-gray-900">{popoverTitle}</h3>
          </div>

          {/* Grid Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOption(option.value)}
                  className={cn(
                    "group relative flex items-start space-x-3 py-3 rounded-xl transition-all text-left w-full border border-transparent",
                    isSelected
                      ? "bg-cyan-50/60 border-cyan-200"
                      : "hover:bg-gray-50 hover:border-gray-100"
                  )}
                >
                  {/* Icon Wrapper */}
                  {option.icon && (
                    <div
                      className={cn(
                        "p-2 rounded-lg shrink-0 transition-colors",
                      )}
                    >
                      <img
                        src={option.icon}
                        alt="Map icon"
                        className="h-6 w-6"
                      />
                    </div>
                  )}

                  {/* Text Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-md font-semibold text-gray-900 truncate">
                        {option.label}
                      </p>
                    </div>
                    {option.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                        {option.description}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";